import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import StudyResourceService, { StudyResource } from "../../_services/StudyResourceService";
import ClinicalMastersService, {
  ClinicalMasters,
  MasterCondition,
  MasterSource,
  MasterParameter,
  MasterMedicationClass,
} from "../../_services/ClinicalMastersService";
import { STUDY_RESOURCE_TYPE } from "../../_constants/studyResourceType";
import { ListEditor, SpecRef } from "./components/ClinicalSpecSections";
import "./StudyResourcesPage.css";

/**
 * Clinical Domains — management surface for the first-class clinical layer:
 *
 *   clinical_domain (therapeutic areas) ←M2M+primary→ clinical_condition
 *   clinical_source (+ documents = study resources) ← condition references
 *   clinical_parameter (LOINC vocabulary) · medication_class (ATC-keyed)
 *   shared governed config (§6 rule classes, §8–§12 artifacts, roles)
 *
 * All backed by real tables via /v1/clinical-masters (bulk-sync save).
 * Reference/decision-resource counts per condition are derived live from type-40
 * resources tagged `config.condition = <conditionKey>`.
 */

const parseConfig = (content?: string | null): any => {
  try { return JSON.parse(content || "{}"); } catch { return {}; }
};

const ClinicalDomainsPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();

  const [masters, setMasters] = useState<ClinicalMasters | null>(null);
  const [resources, setResources] = useState<StudyResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  /** View-first: the vocabulary every resource references opens read-only;
   *  managing is an explicit, confirmed act (platform-governance surface). */
  const [manageMode, setManageMode] = useState(false);
  /** Identity codes captured at load — immutable once set (other layers join on them). */
  const [lockedCodes, setLockedCodes] = useState<Set<string>>(new Set());
  const [lockedAtc, setLockedAtc] = useState<Set<string>>(new Set());

  // Linked-document resolution + the per-source link flow
  const [linkedDocs, setLinkedDocs] = useState<Record<number, { name: string; isRagified?: boolean }>>({});
  const [linkingIdx, setLinkingIdx] = useState<number | null>(null);
  const [docSearch, setDocSearch] = useState("");
  const [docResults, setDocResults] = useState<StudyResource[]>([]);
  const [docSearching, setDocSearching] = useState(false);

  const domainFilter = useMemo(
    () => new URLSearchParams(location.search).get("domain"),
    [location.search],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [m, res] = await Promise.all([
        ClinicalMastersService.getClinicalMasters(),
        StudyResourceService.getStudyResources({
          typeId: STUDY_RESOURCE_TYPE.INTERACTIVE_PATHWAY,
          sortBy: "lastModifiedDate",
          sortOrder: "desc",
          limit: 200,
        }),
      ]);
      setMasters(m);
      setResources(res.data || []);
      setLockedCodes(new Set(m.parameters.filter((p) => p.code).map((p) => p.paramKey)));
      setLockedAtc(new Set(m.medicationClasses.filter((c) => c.atcCode).map((c) => c.classKey)));
      setDirty(false);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to load clinical masters");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Resolve linked document names + RAG state via the list API
  useEffect(() => {
    const ids = Array.from(new Set((masters?.sources || []).flatMap((s) => s.resourceIds || [])));
    const missing = ids.filter((id) => !(id in linkedDocs));
    if (missing.length === 0) return;
    let cancelled = false;
    Promise.all(
      missing.map(async (id) => {
        try {
          const res = await StudyResourceService.getStudyResources({ id, limit: 1 });
          const r = res.data?.[0];
          return [id, { name: r?.name || `#${id}`, isRagified: r?.isRagified }] as const;
        } catch {
          return [id, { name: `#${id}` }] as const;
        }
      }),
    ).then((entries) => {
      if (!cancelled) setLinkedDocs((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
    });
    return () => { cancelled = true; };
  }, [masters, linkedDocs]);

  const update = (patch: Partial<ClinicalMasters>) => {
    if (!masters || !manageMode) return; // view mode: every mutation no-ops at the single choke point
    setMasters({ ...masters, ...patch });
    setDirty(true);
  };

  /** How many decision resources reference a master key (quoted-string scan of their config JSON). */
  const refCount = useCallback(
    (...keys: Array<string | undefined>) =>
      resources.filter((r) => keys.some((k) => k && (r.content || "").includes(`"${k}"`))).length,
    [resources],
  );
  const updateCondition = (i: number, patch: Partial<MasterCondition>) =>
    update({ conditions: masters!.conditions.map((c, x) => (x === i ? { ...c, ...patch } : c)) });
  const updateSource = (i: number, patch: Partial<MasterSource>) =>
    update({ sources: masters!.sources.map((s, x) => (x === i ? { ...s, ...patch } : s)) });
  const updateParameter = (i: number, patch: Partial<MasterParameter>) =>
    update({ parameters: masters!.parameters.map((p, x) => (x === i ? { ...p, ...patch } : p)) });
  const updateMedClass = (i: number, patch: Partial<MasterMedicationClass>) =>
    update({ medicationClasses: masters!.medicationClasses.map((m, x) => (x === i ? { ...m, ...patch } : m)) });

  const save = async () => {
    if (!masters) return;
    setSaving(true);
    setError(null);
    try {
      await ClinicalMastersService.saveClinicalMasters(masters);
      setDirty(false);
      setJustSaved(true);
      window.setTimeout(() => setJustSaved(false), 2500);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const searchDocs = async () => {
    setDocSearching(true);
    try {
      const res = await StudyResourceService.getStudyResources({ search: docSearch, limit: 10 });
      setDocResults((res.data || []).filter((r) => !parseConfig(r.content).isDomainMaster));
    } catch {
      setDocResults([]);
    } finally {
      setDocSearching(false);
    }
  };

  // Live catalog: reference resources and client/working decision resources per condition key
  const byCondition = useMemo(() => {
    const map = new Map<string, { packs: number; drs: number }>();
    let untagged = 0;
    resources.forEach((r) => {
      const cfg = parseConfig(r.content);
      if (cfg.isDomainMaster) return;
      const cond = cfg.condition as string | undefined;
      if (!cond) { untagged++; return; }
      if (!map.has(cond)) map.set(cond, { packs: 0, drs: 0 });
      if (cfg.isBaseTemplate) map.get(cond)!.packs++;
      else map.get(cond)!.drs++;
    });
    return { map, untagged };
  }, [resources]);

  /** RAG state derived from linked documents; null → no documents, manual value applies. */
  const computedRag = (s: MasterSource): "ragified" | "pending" | null => {
    if (!s.resourceIds || s.resourceIds.length === 0) return null;
    return s.resourceIds.every((id) => linkedDocs[id]?.isRagified) ? "ragified" : "pending";
  };

  if (loading) {
    return <div className="drs-page"><div className="drs-page-loading"><Spinner animation="border" /> Loading clinical masters…</div></div>;
  }

  if (!masters) {
    return (
      <div className="drs-page">
        <div className="drs-page-hero"><div><h1>🏥 Clinical Domains</h1></div></div>
        {error && <Alert variant="danger" style={{ fontSize: 13 }}>{error}</Alert>}
        <div className="drs-upgrade-panel">
          <div className="drs-upgrade-title">Clinical masters unavailable</div>
          <p>The clinical-masters API did not respond. Confirm nodefork is running and the
            0002 migration has been applied to this tenant schema.</p>
          <Button type="button" size="sm" variant="primary" onClick={load}>Retry</Button>
        </div>
      </div>
    );
  }

  const shared = masters.shared || {};
  const referencedSourceKeys = new Set(masters.conditions.flatMap((c) => c.sourceKeys || []));
  const visibleConditions = masters.conditions.filter(
    (c) => !domainFilter || c.domains.some((l) => l.domainKey === domainFilter),
  );

  return (
    <div className={"drs-page" + (manageMode ? "" : " masters-view")}>
      <div className="drs-page-hero">
        <div>
          <h1>🏥 Clinical Domains</h1>
          <div className="drs-page-hero-sub">
            Therapeutic areas, conditions, and the governed masters every decision resource references — first-class tables
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          {!manageMode ? (
            <>
              <span className="drs-chip" title="This vocabulary is referenced by every decision resource — the page opens read-only">🔒 View mode</span>
              <Button
                type="button" size="sm" variant="secondary"
                onClick={() => {
                  if (window.confirm(
                    "Manage the clinical masters?\n\nChanges here affect EVERY decision resource that references this vocabulary — parameters, medication classes, sources, roles. Identity keys and codes stay immutable; referenced records cannot be deleted.",
                  )) setManageMode(true);
                }}
              >
                Manage masters…
              </Button>
            </>
          ) : (
            <>
              <span className="drs-chip drs-chip-warn">✎ Managing platform vocabulary</span>
              <span className="drs-page-status">{saving ? "Saving…" : dirty ? "Unsaved changes" : justSaved ? "✓ Saved" : ""}</span>
              <Button type="button" size="sm" variant="primary" disabled={!dirty || saving} onClick={save}>
                {saving ? <Spinner size="sm" animation="border" style={{ width: 12, height: 12, borderWidth: 2 }} /> : "Save"}
              </Button>
            </>
          )}
        </div>
      </div>

      {error && <Alert variant="danger" style={{ fontSize: 13 }}>{error}</Alert>}

      {/* ── Domains (therapeutic areas) ── */}
      <div className="drs-section-label">Therapeutic-area domains</div>
      <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
        <button
          type="button"
          className={"drs-tab" + (!domainFilter ? " active" : "")}
          onClick={() => history.replace("/clinical-domains")}
        >
          All
        </button>
        {masters.domains.map((d) => (
          <button
            key={d.domainKey}
            type="button"
            className={"drs-tab" + (domainFilter === d.domainKey ? " active" : "")}
            title={d.description || d.domainKey}
            onClick={() => history.replace(`/clinical-domains?domain=${encodeURIComponent(d.domainKey)}`)}
          >
            {d.name}
            <span className="drs-tab-badge">
              {masters.conditions.filter((c) => c.domains.some((l) => l.domainKey === d.domainKey)).length}
            </span>
          </button>
        ))}
        {manageMode && (
          <Button
            type="button" size="sm" variant="secondary"
            onClick={() => {
              const name = window.prompt("New domain name (e.g. Endocrine):");
              if (!name?.trim()) return;
              const key = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-");
              if (masters.domains.some((d) => d.domainKey === key)) return;
              update({ domains: [...masters.domains, { domainKey: key, name: name.trim() }] });
            }}
          >
            + Add domain
          </Button>
        )}
      </div>

      {/* ── Conditions ── */}
      <div className="drs-section-label" style={{ marginTop: 16 }}>
        Conditions {domainFilter ? <small>(in {domainFilter})</small> : <small>(each grows its own reference and working decision resources)</small>}
      </div>
      <div className="drs-card-grid" style={{ marginBottom: 8 }}>
        {visibleConditions.map((c) => {
          const i = masters.conditions.indexOf(c);
          const live = byCondition.map.get(c.conditionKey) || { packs: 0, drs: 0 };
          return (
            <div key={c.conditionKey} className={"drs-resource-card" + (c.status === "active" ? " drs-resource-card-base" : "")} style={{ cursor: "default" }}>
              <div className="drs-resource-card-head">
                <Form.Control size="sm" style={{ fontWeight: 700, maxWidth: 220 }} value={c.name}
                  onChange={(e) => updateCondition(i, { name: e.target.value })} />
                <Form.Select size="sm" style={{ maxWidth: 110 }} value={c.status}
                  onChange={(e) => updateCondition(i, { status: e.target.value as MasterCondition["status"] })}>
                  <option value="active">active</option>
                  <option value="planned">planned</option>
                </Form.Select>
              </div>
              <code style={{ fontSize: 11, color: "var(--text-faint)" }}>{c.conditionKey}</code>

              {/* Domain links: M2M with one primary (★). Click a chip to make it primary. */}
              <div className="cs-inline-label" style={{ margin: "2px 0 0" }}>Domains <small>(★ = primary — click a chip to change)</small></div>
              <div className="cs-list">
                {c.domains.map((l) => (
                  <span
                    key={l.domainKey}
                    className="cs-list-item"
                    style={{ cursor: "pointer", fontWeight: l.isPrimary ? 700 : 400 }}
                    title={l.isPrimary ? "Primary domain" : "Click to make primary"}
                    onClick={() => updateCondition(i, { domains: c.domains.map((x) => ({ ...x, isPrimary: x.domainKey === l.domainKey })) })}
                  >
                    {l.isPrimary ? "★ " : ""}{masters.domains.find((d) => d.domainKey === l.domainKey)?.name ?? l.domainKey}
                    <button type="button" className="cs-list-del" title="Unlink domain"
                      onClick={(e) => { e.stopPropagation(); updateCondition(i, { domains: c.domains.filter((x) => x.domainKey !== l.domainKey) }); }}>×</button>
                  </span>
                ))}
                <Form.Select size="sm" style={{ width: 170, fontSize: 11.5 }} value=""
                  onChange={(e) => {
                    if (e.target.value) updateCondition(i, { domains: [...c.domains, { domainKey: e.target.value, isPrimary: c.domains.length === 0 }] });
                  }}>
                  <option value="">+ link domain…</option>
                  {masters.domains.filter((d) => !c.domains.some((l) => l.domainKey === d.domainKey)).map((d) => (
                    <option key={d.domainKey} value={d.domainKey}>{d.name}</option>
                  ))}
                </Form.Select>
              </div>

              <Form.Control as="textarea" rows={2} style={{ fontSize: 12 }} placeholder="Description" value={c.description || ""}
                onChange={(e) => updateCondition(i, { description: e.target.value })} />

              <div className="cs-inline-label" style={{ margin: "2px 0 0" }}>Guideline sources <SpecRef r="§5" /></div>
              <div className="cs-list">
                {(c.sourceKeys || []).map((sk) => {
                  const src = masters.sources.find((s) => s.sourceKey === sk);
                  return (
                    <span key={sk} className="cs-list-item" title={src ? `${src.type} · ${src.approvalStatus}${src.publisher ? ` · ${src.publisher}` : ""}` : "not in registry"}>
                      {src?.approvalStatus === "approved" ? "✓ " : src ? "◌ " : "⚠ "}{src?.name ?? sk}
                      <button type="button" className="cs-list-del" title="Remove reference"
                        onClick={() => updateCondition(i, { sourceKeys: (c.sourceKeys || []).filter((x) => x !== sk) })}>×</button>
                    </span>
                  );
                })}
                <Form.Select size="sm" style={{ width: 200, fontSize: 11.5 }} value=""
                  onChange={(e) => { if (e.target.value) updateCondition(i, { sourceKeys: [...(c.sourceKeys || []), e.target.value] }); }}>
                  <option value="">+ add from registry…</option>
                  {masters.sources.filter((s) => !(c.sourceKeys || []).includes(s.sourceKey)).map((s) => (
                    <option key={s.sourceKey} value={s.sourceKey}>{s.name}</option>
                  ))}
                </Form.Select>
              </div>

              <div className="drs-resource-card-chips">
                <span
                  className={"drs-chip" + (live.packs > 0 ? " drs-chip-exec" : "")}
                  title="Type-40 decision resources tagged with this condition; the parenthetical counts those flagged as governed SynerGen references (isBaseTemplate)"
                >
                  {live.packs + live.drs} decision resource{live.packs + live.drs === 1 ? "" : "s"}
                  {live.packs > 0 && ` (${live.packs} SynerGen reference${live.packs === 1 ? "" : "s"})`}
                </span>
              </div>
              <div className="drs-base-actions">
                <Button type="button" size="sm" variant="primary" disabled={live.packs + live.drs === 0}
                  onClick={() => history.push(`/decision-resource-studio?condition=${encodeURIComponent(c.conditionKey)}`)}>
                  Open studio →
                </Button>
                {live.packs + live.drs === 0 && <span className="drs-hint" style={{ margin: 0, alignSelf: "center" }}>no resources yet</span>}
              </div>
            </div>
          );
        })}
      </div>
      {manageMode && <Button type="button" size="sm" variant="secondary" style={{ marginBottom: 6 }}
        onClick={() => {
          const name = window.prompt("New condition name (e.g. Chronic Kidney Disease):");
          if (!name?.trim()) return;
          const key = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-");
          if (masters.conditions.some((x) => x.conditionKey === key)) return;
          update({
            conditions: [...masters.conditions, {
              conditionKey: key, name: name.trim(), status: "planned", description: "",
              domains: domainFilter ? [{ domainKey: domainFilter, isPrimary: true }] : [],
              sourceKeys: [],
            }],
          });
        }}>
        + Add condition
      </Button>}
      {byCondition.untagged > 0 && (
        <div className="drs-hint">
          {byCondition.untagged} decision resource{byCondition.untagged === 1 ? "" : "s"} carry no condition tag
          (legacy demos and generic playbooks) — tag them via <code>config.condition</code> to place them in the catalog.
        </div>
      )}

      {/* ── Clinical Sources ── */}
      <div className="drs-section-label" style={{ marginTop: 24 }}>Registries &amp; vocabularies <small>(shared masters every decision resource references)</small></div>

      <div className="cs-sect">
        <div className="cs-sect-head">
          <span className="cs-sect-title">Clinical Sources</span>
          <SpecRef r="§5 · §13.3 · §18.1" />
          <span className="cs-sect-hint">the master records the §18.1 traceability chain starts from; RAG state derives from linked documents</span>
        </div>
        {masters.sources.map((s, i) => (
          <div key={s.sourceKey} className="cs-src-card">
            <div className="cs-src-row">
              <Form.Control className="cs-src-name" size="sm" value={s.name} placeholder="Source name"
                onChange={(e) => updateSource(i, { name: e.target.value })} />
              {computedRag(s) !== null ? (
                <span className={"drs-chip" + (computedRag(s) === "ragified" ? " drs-chip-base" : " drs-chip-warn")}
                  title="Derived from the linked documents' RAG state — RAGify the documents to change it">
                  RAG: {computedRag(s)}
                </span>
              ) : (
                <Form.Select size="sm" style={{ width: 128 }} value={s.ragStatusManual || "pending"}
                  title="Manual RAG state — this source has no linked documents (e.g. a service)"
                  onChange={(e) => updateSource(i, { ragStatusManual: e.target.value })}>
                  <option value="pending">RAG: pending</option>
                  <option value="ragified">RAG: ragified</option>
                  <option value="not-applicable">RAG: n/a</option>
                </Form.Select>
              )}
              <button type="button" className="drs-del"
                disabled={referencedSourceKeys.has(s.sourceKey)}
                style={referencedSourceKeys.has(s.sourceKey) ? { opacity: 0.3, cursor: "not-allowed" } : undefined}
                title={referencedSourceKeys.has(s.sourceKey)
                  ? "Referenced by a condition — set approval to 'retired' instead of deleting"
                  : "Remove source"}
                onClick={() => update({ sources: masters.sources.filter((_, x) => x !== i) })}>×</button>
            </div>
            <div className="cs-src-row cs-src-meta">
              <code className="cs-src-key">{s.sourceKey}</code>
              <span className="drs-mini-label">Publisher</span>
              <Form.Control size="sm" style={{ width: 140 }} value={s.publisher || ""} onChange={(e) => updateSource(i, { publisher: e.target.value })} />
              <span className="drs-mini-label">Year</span>
              <Form.Control size="sm" style={{ width: 66 }} value={s.year || ""} onChange={(e) => updateSource(i, { year: e.target.value })} />
              <span className="drs-mini-label">Type</span>
              <Form.Select size="sm" style={{ width: 168 }} value={s.type} onChange={(e) => updateSource(i, { type: e.target.value })}>
                <option value="guideline">guideline</option>
                <option value="drug-labeling">drug-labeling</option>
                <option value="specialty-guidance">specialty-guidance</option>
                <option value="client-policy">client-policy</option>
              </Form.Select>
              <span className="drs-mini-label">Approval</span>
              <Form.Select size="sm" style={{ width: 112 }} value={s.approvalStatus} onChange={(e) => updateSource(i, { approvalStatus: e.target.value })}>
                <option value="approved">approved</option>
                <option value="draft">draft</option>
                <option value="retired">retired</option>
              </Form.Select>
            </div>
            <div className="cs-src-row">
              <span className="drs-mini-label">Documents</span>
              <div className="cs-list" style={{ flex: 1 }}>
                {(s.resourceIds || []).map((rid) => (
                  <span key={rid} className="cs-list-item" style={{ cursor: "pointer" }}
                    title={`Study resource #${rid} — ${linkedDocs[rid]?.isRagified ? "RAGified" : "not RAGified yet"}. Click to open.`}
                    onClick={() => history.push(`/study-resources?id=${rid}`)}>
                    {linkedDocs[rid]?.isRagified ? "✓" : "◌"} {linkedDocs[rid]?.name ?? `#${rid}`}
                    <button type="button" className="cs-list-del" title="Unlink document"
                      onClick={(e) => { e.stopPropagation(); updateSource(i, { resourceIds: (s.resourceIds || []).filter((x) => x !== rid) }); }}>×</button>
                  </span>
                ))}
                {manageMode && (
                  <Button type="button" size="sm" variant="secondary" style={{ fontSize: 11, padding: "1px 8px" }}
                    onClick={() => { setLinkingIdx(linkingIdx === i ? null : i); setDocSearch(""); setDocResults([]); }}>
                    🔗 link
                  </Button>
                )}
              </div>
            </div>
            {linkingIdx === i && (
              <div className="cs-pathway" style={{ margin: "8px 0 2px" }}>
                <div className="cs-pathway-name">
                  Link documents to “{s.name || s.sourceKey}”
                  <button type="button" className="drs-del" style={{ marginLeft: "auto" }} title="Close" onClick={() => setLinkingIdx(null)}>×</button>
                </div>
                <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                  <Form.Control size="sm" style={{ maxWidth: 320 }} value={docSearch} autoFocus
                    placeholder="Search study resources (e.g. guideline)…"
                    onChange={(e) => setDocSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); searchDocs(); } }} />
                  <Button type="button" size="sm" variant="secondary" disabled={docSearching || !docSearch.trim()} onClick={searchDocs}>
                    {docSearching ? "Searching…" : "Search"}
                  </Button>
                </div>
                {docResults.filter((r) => !(s.resourceIds || []).includes(r.id!)).map((r) => (
                  <div key={r.id} className="drs-rule-card drs-rule-card-muted" style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px" }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600 }}>{r.name}</span>
                    <span className="drs-page-id">#{r.id}</span>
                    {r.typeName && <span className="drs-chip">{r.typeName}</span>}
                    <span className={"drs-chip" + (r.isRagified ? " drs-chip-base" : "")}>{r.isRagified ? "✓ RAGified" : "not RAGified"}</span>
                    <Button type="button" size="sm" variant="primary" style={{ marginLeft: "auto", fontSize: 11, padding: "2px 10px" }}
                      onClick={() => {
                        updateSource(i, { resourceIds: [...(s.resourceIds || []), r.id!] });
                        setLinkedDocs((prev) => ({ ...prev, [r.id!]: { name: r.name, isRagified: r.isRagified } }));
                      }}>
                      Link
                    </Button>
                  </div>
                ))}
                {!docSearching && docSearch && docResults.length === 0 && (
                  <div className="drs-hint">No matches — upload the document via Study Resources first, then link it here.</div>
                )}
              </div>
            )}
          </div>
        ))}
        {manageMode && <Button type="button" size="sm" variant="secondary" style={{ marginTop: 8 }}
          onClick={() => {
            const name = window.prompt("New source name:");
            if (!name?.trim()) return;
            let key = "src-" + name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 48);
            while (masters.sources.some((s) => s.sourceKey === key)) key = `${key}-x`;
            update({ sources: [...masters.sources, { sourceKey: key, name: name.trim(), type: "guideline", approvalStatus: "draft", resourceIds: [] }] });
          }}>
          + Add source
        </Button>}
      </div>

      {/* ── Clinical Parameters ── */}
      <div className="cs-sect">
        <div className="cs-sect-head">
          <span className="cs-sect-title">Clinical Parameters</span>
          <SpecRef r="§12.1 · §16.2" />
          <span className="cs-sect-hint">the controlled vocabulary of observables (LOINC-coded) that structured rules and capture fields reference</span>
        </div>
        <div className="drs-matrix-wrap">
          <table className="cs-table cs-table-bordered">
            <thead>
              <tr>
                <th style={{ width: 150 }}>Key</th><th>Name</th><th style={{ width: 78 }}>Abbr</th><th style={{ width: 96 }}>Code</th>
                <th style={{ width: 88 }}>System</th><th style={{ width: 98 }}>Type</th>
                <th style={{ width: 100 }}>Units</th><th style={{ width: 105 }}>Category</th><th style={{ width: 30 }}></th>
              </tr>
            </thead>
            <tbody>
              {masters.parameters.map((p, i) => {
                const used = refCount(p.paramKey, p.name);
                const codeLocked = lockedCodes.has(p.paramKey);
                return (
                <tr key={p.paramKey}>
                  <td><code style={{ fontSize: 11 }} title="Identity key — immutable; rules, guards and capture fields join on it">{p.paramKey}</code></td>
                  <td><Form.Control size="sm" value={p.name} onChange={(e) => updateParameter(i, { name: e.target.value })} /></td>
                  <td><Form.Control size="sm" value={p.abbreviation || ""} onChange={(e) => updateParameter(i, { abbreviation: e.target.value })} /></td>
                  <td><Form.Control size="sm" value={p.code || ""} disabled={codeLocked}
                    title={codeLocked ? "Code is identity — immutable once set. Wrong code? Retire this parameter and create a new one." : undefined}
                    onChange={(e) => updateParameter(i, { code: e.target.value })} /></td>
                  <td><Form.Control size="sm" value={p.codeSystem || ""} disabled={codeLocked}
                    title={codeLocked ? "Locked with the code" : undefined}
                    onChange={(e) => updateParameter(i, { codeSystem: e.target.value })} /></td>
                  <td>
                    <Form.Select size="sm" value={p.dataType} onChange={(e) => updateParameter(i, { dataType: e.target.value })}>
                      <option value="numeric">numeric</option>
                      <option value="boolean">boolean</option>
                      <option value="coded">coded</option>
                      <option value="text">text</option>
                    </Form.Select>
                  </td>
                  <td><Form.Control size="sm" value={p.units || ""} onChange={(e) => updateParameter(i, { units: e.target.value })} /></td>
                  <td><Form.Control size="sm" value={p.category || ""} placeholder="lab / vital / history" onChange={(e) => updateParameter(i, { category: e.target.value })} /></td>
                  <td><button type="button" className="drs-del" disabled={used > 0}
                    style={used > 0 ? { opacity: 0.3, cursor: "not-allowed" } : undefined}
                    title={used > 0 ? `Referenced by ${used} decision resource(s) — cannot delete` : "Remove parameter"}
                    onClick={() => { if (used === 0) update({ parameters: masters.parameters.filter((_, x) => x !== i) }); }}>×</button></td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {manageMode && <Button type="button" size="sm" variant="secondary" style={{ marginTop: 8 }}
          onClick={() => {
            const name = window.prompt("New parameter name (e.g. Serum magnesium):");
            if (!name?.trim()) return;
            let key = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 48);
            while (masters.parameters.some((p) => p.paramKey === key)) key = `${key}_x`;
            update({ parameters: [...masters.parameters, { paramKey: key, name: name.trim(), dataType: "numeric" }] });
          }}>
          + Add parameter
        </Button>}
      </div>

      {/* ── Medication Classes ── */}
      <div className="cs-sect">
        <div className="cs-sect-head">
          <span className="cs-sect-title">Medication Classes</span>
          <SpecRef r="§9.2" />
          <span className="cs-sect-hint">ATC-keyed so medication lists (RxNorm→ATC) and formularies can join; referenced by matrix rows and class guardrails</span>
        </div>
        <div className="drs-matrix-wrap">
          <table className="cs-table cs-table-bordered">
            <thead>
              <tr><th style={{ width: 150 }}>Key</th><th style={{ width: 230 }}>Name</th><th style={{ width: 90 }}>ATC</th><th>Description</th><th style={{ width: 30 }}></th></tr>
            </thead>
            <tbody>
              {masters.medicationClasses.map((m, i) => {
                const used = refCount(m.classKey, m.name);
                const atcLocked = lockedAtc.has(m.classKey);
                return (
                <tr key={m.classKey}>
                  <td><code style={{ fontSize: 11 }} title="Identity key — immutable; matrix rows and formularies join on it">{m.classKey}</code></td>
                  <td><Form.Control size="sm" value={m.name} onChange={(e) => updateMedClass(i, { name: e.target.value })} /></td>
                  <td><Form.Control size="sm" value={m.atcCode || ""} disabled={atcLocked}
                    title={atcLocked ? "ATC code is identity — immutable once set. Wrong code? Retire this class and create a new one." : undefined}
                    onChange={(e) => updateMedClass(i, { atcCode: e.target.value })} /></td>
                  <td><Form.Control size="sm" value={m.description || ""} onChange={(e) => updateMedClass(i, { description: e.target.value })} /></td>
                  <td><button type="button" className="drs-del" disabled={used > 0}
                    style={used > 0 ? { opacity: 0.3, cursor: "not-allowed" } : undefined}
                    title={used > 0 ? `Referenced by ${used} decision resource(s) — cannot delete` : "Remove class"}
                    onClick={() => { if (used === 0) update({ medicationClasses: masters.medicationClasses.filter((_, x) => x !== i) }); }}>×</button></td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {manageMode && <Button type="button" size="sm" variant="secondary" style={{ marginTop: 8 }}
          onClick={() => {
            const name = window.prompt("New medication class name:");
            if (!name?.trim()) return;
            let key = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 48);
            while (masters.medicationClasses.some((m) => m.classKey === key)) key = `${key}-x`;
            update({ medicationClasses: [...masters.medicationClasses, { classKey: key, name: name.trim() }] });
          }}>
          + Add medication class
        </Button>}
      </div>

      {/* ── Shared governed config ── */}
      <div className="cs-sect">
        <div className="cs-sect-head">
          <span className="cs-sect-title">Rule Classes</span>
          <SpecRef r="§6" />
          <span className="cs-sect-hint">every executable rule in any decision resource carries one of these — defined by the requirements document, not editable here</span>
        </div>
        {(shared.ruleClasses || []).map((rc) => (
          <div key={rc.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 8 }}>
            <span className={`cs-class cs-class-${rc.id.toLowerCase()}`} style={{ marginTop: 2 }}>{rc.id}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700 }}>{rc.name} <SpecRef r={rc.specRef} /></div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5 }}>{rc.definition}</div>
            </div>
          </div>
        ))}
        <div className="cs-note">
          🛡 The §6 vocabulary is governance bedrock — the meaning of "Class A" is what makes every A badge in the
          studio binding. Changing these definitions is a specification revision, not a configuration edit.
        </div>
      </div>

      <div className="cs-sect">
        <div className="cs-sect-head">
          <span className="cs-sect-title">Specification Artifacts</span>
          <SpecRef r="§8–§12" />
          <span className="cs-sect-hint">the five governed shapes every condition's decision resources must fill — the studio portlets render them</span>
        </div>
        <div className="cs-static-chips">
          {(shared.specificationArtifacts || []).map((a) => (
            <span key={a.id} className="drs-chip">{a.name} <SpecRef r={a.specRef} /></span>
          ))}
        </div>
      </div>

      <div className="cs-sect">
        <div className="cs-sect-head">
          <span className="cs-sect-title">Clinical Roles</span>
          <span className="cs-sect-hint">the vocabulary authority matrices and workflow guards draw from</span>
        </div>
        <ListEditor
          items={shared.roles || []}
          disabled={!manageMode}
          onChange={(v) => {
            const removed = (shared.roles || []).filter((r) => !v.includes(r));
            const blocked = removed.find((r) => refCount(r) > 0);
            if (blocked) {
              window.alert(`"${blocked}" is referenced by ${refCount(blocked)} decision resource(s) (authority guards / step actors) — cannot remove.`);
              return;
            }
            update({ shared: { ...shared, roles: v } });
          }}
          placeholder="e.g. pharmacist"
        />
      </div>
    </div>
  );
};

export default ClinicalDomainsPage;
